/* eslint-disable array-callback-return, consistent-return */
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { keyBy } from 'lodash';
import objectHash from 'object-hash';
import querystring from 'query-string';
import moment from 'moment';
import { DEFAULT_IMAGE_URLS, SEGMENT_EVENTS } from '../../../../shared/Constants';
import { setLocalStorage, getLocalStorage } from '../../../../shared/actions/index';
import LoadingIndicator from '../../../../shared/components/LoadingIndicator';

import MyPathwayMilestones from './MyPathwayMilestones';
import MyPathwayMobileView from './MyPathwayMobileView';
import MyPathwaySelect from './MyPathwaySelect';
import MyPathwayUpNextCard from './MyPathwayUpNextCard';
import { RuntimeContext } from '../../../../../common/RuntimeContext';

const MyPathway = (props) => {
    const config = useContext(RuntimeContext);
    const {
        userPathway,
        collectionsById,
        userCollectionsById = {},
        onClickBack = () => {},
        loggedInUserCompany,
        isVerbAdmin,
        isMobile,
        history,
        pathways = [],
        toggleCalendarModal,
        isTeamPage = false,
        pathwayOptions = [],
        teamsById = {},
        handleShowCompletedPathways = () => {},
        showCompletedPathways = false,
        completedPathways = [],
        updateUserPathway,
        setCurrentUserPathway = () => {},
        updateUserPathwayContentItem = () => {},
        featuredRecommendedData
    } = props;

    const [upNext, setUpNext] = useState({});
    const [workspaceInfo, setWorkspaceInfo] = useState({});
    const [buttonText, setButtonText] = useState('Begin');
    const [showPathway, setShowPathway] = useState(!isMobile);
    const [currentWidth] = useState(window.outerWidth);
    const [pathwayToDisplay, setPathwayToDisplay] = useState(userPathway);
    const [selectOptions, setSelectOptions] = useState({});
    const [userPathwayDateEdited, setUserPathwayDateEdited] = useState(null);
    const [modalLoadingOverlay, setModalLoadingOverlay] = useState(false);

    const SHOW_COMPLETED_PATHWAYS = 'Show completed pathways';
    const HIDE_COMPLETED_PATHWAYS = 'Hide completed pathways';
    const setUpNextCollectionCard = (userPathwayContent, userPathwayId = '') => {
        const ctaTextByStatus = {
            ASSIGNED: 'Begin',
            STARTED: 'Continue',
            IN_PROGRESS: 'Continue'
        };
        let pathwayIsCompleted = true;
        for (let i = 0; i < userPathwayContent.length; i += 1) {
            const userPathwayContentItem = userPathwayContent[i];
            const currentId = userPathwayContentItem.userSubscriptionId;
            let userCollection = userCollectionsById[currentId] || {};
            const collection = collectionsById[userPathwayContentItem.parentId || userPathwayContentItem.id];

            if (i === 0 && collection && !userPathwayContentItem.isCompleted) {
                if (!currentId) {
                    Object.keys(userCollectionsById).forEach((id) => {
                        if (userCollectionsById[id].collectionId === collection._id) {
                            userCollection = userCollectionsById[id];
                        }
                    });
                }
                setUpNext(collection); // Setting upNext to the first collection as a default
                if (collection.contentIds && collection.contentIds[0]) {
                    setWorkspaceInfo({
                        workspaceLink: `/assetlink/${collection.contentIds[0].id}`,
                        userCollectionId: userCollection._id,
                        currentUserCollection: {
                            currentCollectionId: collection._id,
                            id: userCollection._id
                        }
                    });
                }
            }
            if (!userPathwayContentItem.isCompleted) {
                pathwayIsCompleted = false;
                if (
                    userPathwayContentItem.parentType === 'COLLECTION' &&
                    (userCollection.status === 'IN_PROGRESS' || userCollection.status === 'ASSIGNED') &&
                    collection &&
                    userCollection.content
                ) {
                    for (let i = 0; i < userCollection.content.length; i += 1) {
                        const currentContent = userCollection.content[i];
                        if (!currentContent.isCompleted && currentContent.learningAssetId) {
                            setWorkspaceInfo({
                                workspaceLink: `/assetlink/${currentContent.learningAssetId}`,
                                userCollectionId: userCollection._id,
                                currentUserCollection: {
                                    currentCollectionId: collection._id,
                                    id: userCollection._id
                                }
                            });
                            break;
                        }
                    }
                    setUpNext(collection);
                    setButtonText(ctaTextByStatus[userCollection.status]);
                    break;
                } else if (userPathwayContentItem.type === 'EVENT') {
                    setUpNext({
                        thumbnailImageUrl: DEFAULT_IMAGE_URLS.PATHWAY_EVENT_THUMBNAIL_GREY,
                        name: userPathwayContentItem.name,
                        subtitle: userPathwayContentItem.shortDescription,
                        dateTime: `${moment(userPathwayContent[i].dateTime).format('M/D @h:mma')}`.toUpperCase(),
                        urlLink: userPathwayContentItem.urlLink,
                        type: 'EVENT',
                        index: i
                    });
                    setButtonText('MARK COMPLETE');
                    break;
                } else if (userPathwayContentItem.type === 'TASK') {
                    const upNextInfo = {
                        thumbnailImageUrl: DEFAULT_IMAGE_URLS.PATHWAY_TASK_THUMBNAIL,
                        name: userPathwayContentItem.name,
                        subtitle: userPathwayContentItem.shortDescription,
                        dateTime: `DUE ${moment(userPathwayContent[i].dateTime).format('M/D')}`,
                        type: 'TASK',
                        index: i,
                        isCompleted: userPathwayContent.isCompleted,
                        status: userPathwayContentItem.status
                    };
                    if (userPathwayContentItem.dateTime) {
                        upNextInfo.dateTime = `DUE ${moment(userPathwayContent[i].dateTime).format('M/D')}`;
                    }
                    setButtonText('MARK COMPLETE');
                    setUpNext(upNextInfo);
                    break;
                } else if (
                    userPathwayContentItem.name &&
                    userPathwayContentItem.shortDescription &&
                    userPathwayContent[i].dateTime
                ) {
                    setUpNext({
                        thumbnailImageUrl: DEFAULT_IMAGE_URLS.PATHWAY_EVENT_THUMBNAIL_GREY,
                        name: userPathwayContentItem.name,
                        subtitle: userPathwayContentItem.shortDescription,
                        dateTime: `${moment(userPathwayContent[i].dateTime).format('M/D @h:mma')}`.toUpperCase(),
                        urlLink: userPathwayContentItem.urlLink
                    });
                    if (userPathwayContentItem.urlCustomName) {
                        setButtonText(userPathwayContentItem.urlCustomName);
                    }
                    if (userPathwayContentItem.urlLink) {
                        setButtonText('OPEN LINK');
                    }
                    break;
                }
            }
        }

        if (pathwayIsCompleted) {
            setUpNext({ isCompleted: true, userPathwayId });
        }
    };

    const setShowEditedContentMessage = (userPathway) => {
        const userPathwayContentEvents = userPathway.contentEvents || [];
        let dateAvailable = false;
        if (userPathwayContentEvents.length) {
            const lastEdit = userPathwayContentEvents[userPathwayContentEvents.length - 1];
            const today = new Date();
            const sevenDaysAgo = new Date(new Date().setDate(today.getDate() - 7));
            if (new Date(lastEdit.editedOn) > sevenDaysAgo) {
                const formattedDate = moment(lastEdit.editedOn).format('M/D/YY');
                dateAvailable = true;
                setUserPathwayDateEdited(formattedDate);
            }
        }
        if (userPathwayDateEdited && !dateAvailable) {
            setUserPathwayDateEdited('');
        }
    };

    useEffect(() => {
        let userPathwayContent = (userPathway || pathwayToDisplay || pathwayOptions[0]).content || [];
        const localSelectedPathway = getLocalStorage({ key: 'selectedPathway', dispatchDisabled: true });
        const { userPathwayId = '' } = querystring.parse(history.location.search);
        if (localSelectedPathway && !userPathwayId) {
            const pathwayOptionsById = keyBy(pathwayOptions, ({ _id }) => _id);
            if (pathwayOptionsById[localSelectedPathway]) {
                setPathwayToDisplay(pathwayOptionsById[localSelectedPathway]);
                userPathwayContent = pathwayOptionsById[localSelectedPathway].content || [];
            }
        }

        if (!pathwayToDisplay.isCompleted) {
            setUpNextCollectionCard(userPathwayContent);
        }
        setShowEditedContentMessage(pathwayToDisplay || pathwayOptions[0] || userPathway);
    }, [objectHash(userCollectionsById), objectHash(collectionsById), upNext._id]);

    useEffect(() => {
        if (isTeamPage) {
            setPathwayToDisplay(userPathway);
            if (!pathwayToDisplay.isCompleted) {
                setUpNextCollectionCard(userPathway.content || []);
            }
        }
    }, [objectHash(userPathway || pathwayToDisplay || pathwayOptions[0])]);

    const setToDefaultPathway = ({ pathway = {}, hidePathways = false }) => {
        if ((pathway?.name === HIDE_COMPLETED_PATHWAYS || hidePathways) && pathwayToDisplay.isCompleted) {
            // selected pathway will now be hidden so default to first in-progress pathway
            const defaultPathway = hidePathways ? pathway : pathwayOptions[0];

            setLocalStorage({ key: 'selectedPathway', value: defaultPathway._id, dispatchDisabled: true });
            if (!pathway?.isCompleted) {
                setUpNextCollectionCard(defaultPathway.content || []);
            }
            setPathwayToDisplay(defaultPathway);
        }
    };

    const handlePathwaySelect = (pathway = {}) => {
        if ([SHOW_COMPLETED_PATHWAYS, HIDE_COMPLETED_PATHWAYS].includes(pathway.name)) {
            setSelectOptions({ menuIsOpen: true });
            setToDefaultPathway({ pathway });
            return handleShowCompletedPathways(pathway.name === SHOW_COMPLETED_PATHWAYS);
        }

        setSelectOptions({});
        handleShowCompletedPathways(false, pathway);
        setCurrentUserPathway(pathway);
        setLocalStorage({ key: 'selectedPathway', value: pathway._id, dispatchDisabled: true });
        setPathwayToDisplay(pathway);
        setUpNextCollectionCard(pathway.content || [], pathway._id);
    };

    const toggleShowPathway = () => {
        setShowPathway(!showPathway);
    };

    const getEventHref = (event) =>
        event.urlLink.indexOf('http://') === 0 || event.urlLink.indexOf('https://') === 0
            ? event.urlLink
            : `http://${event.urlLink}`;

    const CTAClickHandler = () => {
        if (upNext?.urlLink || featuredRecommendedData?.urlLink) {
            const href = getEventHref(upNext && upNext.urlLink ? upNext : featuredRecommendedData);
            return window.open(href);
        }
        if (workspaceInfo.workspaceLink) {
            return history.push(workspaceInfo.workspaceLink, {
                userCollection: {
                    id: workspaceInfo.userCollectionId
                },
                currentUserCollection: { ...workspaceInfo.currentUserCollection }
            });
        }
        return history.push(
            `/collection/${upNext.permalink ||
                upNext._id ||
                featuredRecommendedData.collectionPermalink ||
                featuredRecommendedData.collectionId}`
        );
    };

    const markTaskAsCompleted = (idx, milestone = {}) => {
        const milestoneChecked = pathwayToDisplay?.content[idx] || milestone;
        const updatedMilestone = { ...milestoneChecked };
        updatedMilestone.isCompleted = !updatedMilestone.isCompleted;
        updatedMilestone.status = updatedMilestone.status === 'COMPLETED' ? 'ASSIGNED' : 'COMPLETED';
        const updatedPathway = { ...pathwayToDisplay };
        updatedPathway.content[idx] = updatedMilestone;
        const segmentInfo = {
            dateTime: milestoneChecked.date,
            taskName: milestoneChecked.name,
            pathwayName: pathwayToDisplay.name,
            licenseType: pathwayToDisplay.subscriptionType,
            pathsContext: config.pathsWeb
        };

        setUpNextCollectionCard(updatedPathway.content || []);
        return updateUserPathway(updatedPathway);
    };

    const pathwayOptionObj = {};
    const uniquePathwayOptions = [];
    pathwayOptions.forEach((pathway) => {
        if (!pathwayOptionObj[pathway._id]) {
            uniquePathwayOptions.push(pathway);
            pathwayOptionObj[pathway._id] = true;
        }
    });

    const pathwayOptionsForSelect = [...uniquePathwayOptions];
    if (completedPathways.length > 0) {
        const showCompletedPathwaysOption = showCompletedPathways
            ? { name: HIDE_COMPLETED_PATHWAYS }
            : { name: SHOW_COMPLETED_PATHWAYS };
        pathwayOptionsForSelect.push(showCompletedPathwaysOption);
    }

    const pathwayTypeText = (
        <div className={'pathway-type-text'}>
            {pathwayToDisplay?.teamId || pathwayToDisplay?.pastTeamId ? (
                <>
                    <i className={'fal fa-user-friends fa-lg learner-text-icon'}></i>
                    <span className={'learner-text'}>
                        {pathwayToDisplay.teamId ? (
                            <span>You're doing this pathway with </span>
                        ) : (
                            <span>You did this pathway with </span>
                        )}
                        <span>
                            {teamsById[pathwayToDisplay.teamId || pathwayToDisplay.pastTeamId]?.name ? (
                                <Link
                                    to={`/teams/${pathwayToDisplay.teamId || pathwayToDisplay.pastTeamId}`}
                                    className={'learner-text-link'}
                                >
                                    {teamsById[pathwayToDisplay.teamId || pathwayToDisplay.pastTeamId]?.name}
                                </Link>
                            ) : (
                                'Your Team'
                            )}
                        </span>
                    </span>
                </>
            ) : (
                <>
                    <i className={'fal fa-user-alt fa-lg learner-text-icon'}></i>
                    <span className={'learner-text'}>You're doing this pathway as an individual learner</span>
                </>
            )}
        </div>
    );
    return isMobile ? (
        <MyPathwayMobileView
            isTeamPage={isTeamPage}
            pathwayToDisplay={pathwayToDisplay}
            handlePathwaySelect={handlePathwaySelect}
            pathwayOptionsForSelect={pathwayOptionsForSelect}
            selectOptions={selectOptions}
            setSelectOptions={setSelectOptions}
            setToDefaultPathway={setToDefaultPathway}
            handleShowCompletedPathways={handleShowCompletedPathways}
            upNext={upNext}
            history={history}
            currentWidth={currentWidth}
            buttonText={buttonText}
            showPathway={showPathway}
            userPathwayDateEdited={userPathwayDateEdited}
            loggedInUserCompany={loggedInUserCompany}
            isVerbAdmin={isVerbAdmin}
            updateUserPathway={updateUserPathway}
            toggleCalendarModal={toggleCalendarModal}
            collectionsById={collectionsById}
            userCollectionsById={userCollectionsById}
            getEventHref={getEventHref}
            onClickBack={onClickBack}
            toggleShowPathway={toggleShowPathway}
            CTAClickHandler={CTAClickHandler}
            markTaskAsCompleted={markTaskAsCompleted}
            updateUserPathwayContentItem={updateUserPathwayContentItem}
            setModalLoadingOverlay={setModalLoadingOverlay}
        />
    ) : (
        <div className={'my-pathway-container'}>
            <h3 className={'my-pathways-title'}>Pathways in Progress</h3>
            {!isTeamPage ? (
                <>
                    <MyPathwaySelect
                        setSelectOptions={setSelectOptions}
                        setToDefaultPathway={setToDefaultPathway}
                        handleShowCompletedPathways={handleShowCompletedPathways}
                        handlePathwaySelect={handlePathwaySelect}
                        pathwayOptionsForSelect={pathwayOptionsForSelect}
                        selectOptions={selectOptions}
                        pathwayToDisplay={pathwayToDisplay}
                    />
                    {pathwayTypeText}
                </>
            ) : (
                <div className={'row pathway-name verb-title-3'}>{userPathway.name}</div>
            )}
            {isTeamPage && modalLoadingOverlay ? (
                <div className={'pathway-panel-loading-overlay'}>
                    <LoadingIndicator />
                </div>
            ) : null}
            <MyPathwayUpNextCard
                isTeamPage={isTeamPage}
                upNext={featuredRecommendedData || upNext}
                history={history}
                onClickBack={onClickBack}
                buttonText={buttonText}
                CTAClickHandler={CTAClickHandler}
                markTaskAsCompleted={markTaskAsCompleted}
                updateUserPathwayContentItem={updateUserPathwayContentItem}
                pathwayCompleted={userPathway.isCompleted}
            />
            {userPathwayDateEdited ? (
                <div className={'pathway-edited-text'}>
                    <i className={'fad fa-exclamation-triangle fa-lg pathway-edit-text-icon'}></i>
                    <span className={'edit-text'}>
                        This pathway was updated by an admin on {userPathwayDateEdited}.
                    </span>
                </div>
            ) : null}
            <MyPathwayMilestones
                pathwayToDisplay={pathwayToDisplay}
                loggedInUserCompany={loggedInUserCompany}
                isVerbAdmin={isVerbAdmin}
                toggleCalendarModal={toggleCalendarModal}
                collectionsById={collectionsById}
                userCollectionsById={userCollectionsById}
                upNext={upNext}
                getEventHref={getEventHref}
                markTaskAsCompleted={markTaskAsCompleted}
                updateUserPathwayContentItem={updateUserPathwayContentItem}
                setModalLoadingOverlay={setModalLoadingOverlay}
                updateUserPathway={updateUserPathway}
            />
            {!!pathways.length && !isTeamPage && (
                <>
                    <span className={'change-pathway-text'}>
                        Would you like to start another pathway?
                        <div
                            className={'choose-new-pathway-text'}
                            onClick={() => {
                                window.scrollTo(0, 0);
                                onClickBack('browse');
                            }}
                        >
                            Add a new pathway.
                        </div>
                    </span>
                </>
            )}
        </div>
    );
};

MyPathway.propTypes = {
    userPathway: PropTypes.object,
    collectionsById: PropTypes.object,
    userCollectionsById: PropTypes.object,
    onClickBack: PropTypes.func,
    isMobile: PropTypes.bool,
    loggedInUserCompany: PropTypes.object,
    isVerbAdmin: PropTypes.bool,
    history: PropTypes.object,
    pathways: PropTypes.array,
    toggleCalendarModal: PropTypes.func,
    updateUserPathway: PropTypes.func,
    isTeamPage: PropTypes.bool,
    pathwayOptions: PropTypes.array,
    teamsById: PropTypes.object,
    handleShowCompletedPathways: PropTypes.func,
    showCompletedPathways: PropTypes.bool,
    completedPathways: PropTypes.array,
    setCurrentUserPathway: PropTypes.func,
    updateUserPathwayContentItem: PropTypes.func,
    featuredRecommendedData: PropTypes.object
};

export default MyPathway;
